/* @ts-expect-error: we need to update that once the build that fixes the deprication error is published */
import GoogleMapReact from "google-map-react-tryzens";
import { useEffect, useRef } from "react";

import type { Store } from "~/components/types";
import { storeLocatorAPIKey } from "~/config/stores-config";

const svgMarker = (isHovered: boolean) => {
  const baseStyle = {
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
    anchor: new google.maps.Point(12, 24),
    rotation: 0,
  };

  return {
    ...baseStyle,
    fillColor: isHovered ? "#EA4335" : "#4285F4",
    fillOpacity: 1,
    strokeWeight: isHovered ? 2 : 1,
    strokeColor: "#fff",
    scale: isHovered ? 2.5 : 2,
  };
};

const LeftSideContent = ({
  mapCenter,
  mapZoom,
  shops,
  hoveredStoreId,
  setHoveredStoreId,
}: {
  mapCenter: {
    lat: number;
    lng: number;
  };
  mapZoom: number;
  shops: Store[];
  hoveredStoreId?: string | null;
  setHoveredStoreId: (id: string | null) => void;
}) => {
  const mapRef = useRef<google.maps.Map>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const currentInfoWindow = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!markersRef.current.length || !shops.length) return;

    markersRef.current.forEach((marker, index) => {
      const isHovered = hoveredStoreId === shops[index].id;
      marker.setIcon(svgMarker(isHovered));
    });
  }, [hoveredStoreId]);

  const scrollToStore = (store: Store) => {
    const el = document.querySelector(
      `[data-shop-coordinates="${store.latitude};${store.longitude}"]`,
    );
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderMarkers = (map: google.maps.Map, maps: typeof google.maps) => {
    const bounds = new maps.LatLngBounds();
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (shops && shops.length !== 0) {
      shops.forEach(store => {
        const marker = new maps.Marker({
          position: {
            lat: store.latitude as number,
            lng: store.longitude as number,
          },
          map,
          icon: svgMarker(false),
        });

        markersRef.current.push(marker);

        const content = `
            <strong style="font-size: 16px; margin-top: -12px;">${store.name}</strong>
            <div style="margin-top: 4px;">${store.c_streetName || store.address1 || ""}</div>
        `;

        const infoWindow = new maps.InfoWindow({
          content,
        });

        marker.addListener("click", () => {
          if (currentInfoWindow.current) {
            currentInfoWindow.current.close();
          }

          infoWindow.open(map, marker);
          currentInfoWindow.current = infoWindow;
          setHoveredStoreId(store.id);
          scrollToStore(store);
        });

        infoWindow.addListener("closeclick", () => {
          currentInfoWindow.current = null;
          setHoveredStoreId("");
        });

        bounds.extend(marker.getPosition() as google.maps.LatLng);
      });

      //skip bounding if only 1 store for better ux
      if (shops.length === 1) {
        const marker = shops[0];
        map.setCenter(
          new maps.LatLng(
            marker.latitude as number,
            marker.longitude as number,
          ),
        );
      } else {
        map.fitBounds(bounds);
      }
    }
  };

  return (
    <div className="relative w-full md:w-3/4">
      <div className="sticky top-0 h-[50vh] md:h-screen">
        <div className="h-full w-full">
          <GoogleMapReact
            bootstrapURLKeys={{
              key: storeLocatorAPIKey,
              libraries: ["places", "geometry"],
            }}
            center={mapCenter}
            zoom={mapZoom}
            onGoogleApiLoaded={({
              map,
              maps,
            }: {
              map: google.maps.Map;
              maps: typeof google.maps;
            }) => {
              mapRef.current = map;
              renderMarkers(map, maps);
            }}
            yesIWantToUseGoogleMapApiInternals
            key={JSON.stringify({ shops })}
          />
        </div>
      </div>
    </div>
  );
};

export default LeftSideContent;
